import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Card, Chip, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { BenefitDTOBenefitEnum, BikeleasingContractListItemDTO } from 'probonio-shared-ui/api';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useBikeleasingContracts } from './useBikeleasingDetails';
import { getBikeleasingEmployeeContractStatusColor } from './util/getContractStatusColor';

interface BikeTextProps {
  contract: BikeleasingContractListItemDTO;
}

const ContractListEntry: React.FC<BikeTextProps> = ({ contract }) => {
  const { t } = useTranslation('benefitModule');
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    navigate(`/${BenefitDTOBenefitEnum.BikeleasingV2.toLowerCase()}/contracts/${contract.id}`);
  }, [contract.id, navigate]);

  return (
    <Card variant="outlined" sx={{ mb: 0.5 }} data-test-id="bikeleasing-contract">
      <ListItemButton onClick={handleClick}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <ListItemText
            sx={{
              '& .MuiListItemText-primary': {
                marginBottom: theme => theme.spacing(0.5),
              },
            }}
            primary={
              <Box>
                {
                  <Chip
                    color={getBikeleasingEmployeeContractStatusColor(contract.status)}
                    label={t(`extensions:bikeleasing_v2_benefit.contract.status.${contract.status}`)}
                    variant="filled"
                  ></Chip>
                }
              </Box>
            }
            secondary={
              <Box display="flex" flexDirection="column">
                <Typography variant="h3" component="h3" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                  {contract.bikeBrand}
                </Typography>
                {contract.startDate && contract.endDate && (
                  <Typography variant="body2" component="div">
                    {t('extensions:bikeleasing_v2_benefit.intervall', {
                      start: new Date(contract.startDate),
                      end: new Date(contract.endDate),
                    })}
                  </Typography>
                )}
                {(contract.monthlyPayment || contract.monthlyPayment === 0) && (
                  <Typography variant="body2" component="div">
                    {t('extensions:bikeleasing_v2_benefit.contract.monthlyPayment', {
                      monthlyAmount: contract.monthlyPayment,
                    })}
                  </Typography>
                )}
              </Box>
            }
            slotProps={{
              secondary: { component: 'div' },
            }}
          />
          <ArrowForwardIosIcon></ArrowForwardIosIcon>
        </Box>
      </ListItemButton>
    </Card>
  );
};

export const BikeleasingContracts: React.FC = () => {
  const contracts = useBikeleasingContracts();
  const { t } = useTranslation('benefitModule');

  return contracts && contracts.totalCount ? (
    <>
      <Typography variant="h1" my={1}>
        {t('extensions:bikeleasing_v2_benefit.contract.title')}
      </Typography>

      <List disablePadding>
        {contracts.results.map(contract => (
          <ContractListEntry key={contract.id} contract={contract} />
        ))}
      </List>
    </>
  ) : null;
};
